// React
import { useEffect, useMemo, useState } from 'react';

// Tanstack Query
import { QueryClient } from '@tanstack/react-query';

// Mantine
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';

// Store
import { useCurrentSiteStore } from 'store/SiteStore';

// API
import {
  useGetSiteIntakeItemStatusesCount,
  usePaginateIntakeItemsBySite,
  usePreFetchPaginateIntakeItemsBySite,
} from 'api/resource';

import {
  getUploadStatusGroupCounts,
  isUploadStatusesCountResult,
  UploadStatusGroupCounts,
} from './uploadStatus';

export const useAdminUploadManagementData = (queryClient: QueryClient) => {
  const { id: siteID } = useCurrentSiteStore();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    globalFilter: '',
    columnFilters: [] as MRT_ColumnFiltersState,
    sorting: [] as MRT_SortingState,
  });

  const [nextPage, setNextPage] = useState({
    pageIndex: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    globalFilter: pagination.globalFilter,
    columnFilters: pagination.columnFilters,
    sorting: pagination.sorting,
  });

  useEffect(() => {
    setNextPage({
      pageIndex: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      globalFilter: pagination.globalFilter,
      columnFilters: pagination.columnFilters,
      sorting: pagination.sorting,
    });
  }, [pagination, queryClient]);

  usePreFetchPaginateIntakeItemsBySite(
    queryClient,
    siteID,
    nextPage.pageIndex,
    nextPage.pageSize,
    nextPage.columnFilters,
    nextPage.globalFilter,
    nextPage.sorting
  );

  const intakeItemsResult = usePaginateIntakeItemsBySite({
    siteID,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    columnFilters: pagination.columnFilters,
    globalFilter: pagination.globalFilter,
    sorting: pagination.sorting,
  });

  const { data: taskStatusCountsData, isError: isUploadStatusCountsError } =
    useGetSiteIntakeItemStatusesCount(siteID);

  const statusGroupCounts = useMemo<UploadStatusGroupCounts>(
    () =>
      getUploadStatusGroupCounts(
        !isUploadStatusCountsError && isUploadStatusesCountResult(taskStatusCountsData)
          ? taskStatusCountsData
          : undefined
      ),
    [isUploadStatusCountsError, taskStatusCountsData]
  );

  return {
    items: intakeItemsResult.data?.data || [],
    total: intakeItemsResult.data?.total || 0,
    isLoading: intakeItemsResult.isLoading,
    isFetching: intakeItemsResult.isFetching,
    isError: intakeItemsResult.isError,
    errorMessage: intakeItemsResult.error?.message || '',
    pagination,
    setPagination,
    statusGroupCounts,
  };
};
